import React from 'react';

import type {
  ComponentRenderProps,
  ParagraphProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';
import { renderTextWithIcons } from '../utils/emojiToIcon';

export const ParagraphComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as ParagraphProps;
  const {
    content,
    text,
    size = 'md',
    spacing = 'normal',
    color = 'neutral',
  } = props;
  const manifestId = useManifestId();

  // Support both 'content' and 'text' properties
  const textContent = text || content;

  // Build CSS classes
  const sizeClasses = {
    sm: 'text-sm leading-relaxed',
    md: 'text-base leading-relaxed',
    lg: 'text-lg leading-relaxed',
  };

  const spacingClasses = {
    tight: 'mb-2',
    normal: 'mb-4',
    loose: 'mb-6',
  };

  // Using design system tokens for dark mode support
  const colorClasses = {
    primary: 'text-fg-primary',
    secondary: 'text-fg-tertiary',
    neutral: 'text-fg-primary',
    muted: 'text-fg-quaternary',
  };

  const allClasses = [
    sizeClasses[size] || sizeClasses.md,
    spacingClasses[spacing] || spacingClasses.normal,
    colorClasses[color] || colorClasses.neutral,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Handle line breaks in content and emoji replacement
  const renderContent = () => {
    if (!textContent) {
      return null;
    }
    if (typeof textContent !== 'string') {
      // Unlike ListComponent, this does not extract text/content/label from
      // object-shaped input — paragraph content is never expected to be an
      // object, so it's guarded-and-blanked rather than recovered. Only
      // `list` items get object-shape tolerance, per the production
      // incident that motivated it.
      console.warn(
        'ParagraphComponent: content is not a string',
        typeof textContent
      );
      return null;
    }
    return textContent.split('\n').map((line, index, array) => (
      // eslint-disable-next-line react/no-array-index-key -- Text lines from newline split have no stable identity
      <React.Fragment key={index}>
        {renderTextWithIcons(
          line,
          'w-4 h-4 inline-block relative -top-[2px] mr-1',
          manifestId
        )}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <p className={allClasses} data-component-id={component.id}>
      {renderContent()}
    </p>
  );
};

export default ParagraphComponent;
